/*
 * The "all of one project's blocks" layout shared by the per-student
 * snap-blocks page (snap-blocks.ts) and the snap-grid's all-blocks overlay
 * (snap-grid.ts): every pic in a wrapping row at one zoom, each tagged
 * with where it lives.
 *
 * Zoom is a CSS matter: Snap! floors its block scale at 1, so every pic
 * renders at RENDER_SCALE (2, crisp on HiDPI screens) and the <img>s shrink
 * to the chosen fraction of life size — a zoom change restyles instantly,
 * no re-render.
 */

import type { DefinitionPic } from './snap-render.ts';

export const RENDER_SCALE = 2;

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
  className?: string,
): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (text !== undefined) e.textContent = text;
  if (className !== undefined) e.className = className;
  return e;
};

// Where a pic lives: nothing for a global definition, else the sprite (or
// the stage) it belongs to.
export const ownerTag = (pic: DefinitionPic): string | null =>
  pic.kind === 'definition'
    ? pic.owner === 'global'
      ? null
      : `in sprite ${pic.owner}`
    : pic.owner === 'Stage'
      ? 'on the stage'
      : `in sprite ${pic.owner}`;

// Displayed width of a pic at a zoom: its life-size width times the zoom
// fraction.
const displayWidth = (pic: DefinitionPic, zoom: number): string =>
  `${(pic.width / RENDER_SCALE) * zoom}px`;

// The header line saying how many library-imported definitions the
// renderer left out — '' when none, so an :empty rule can hide the element.
export const libraryNote = (libraryBlocks: number): string =>
  libraryBlocks ? `${libraryBlocks} library block${libraryBlocks === 1 ? '' : 's'} not shown` : '';

/*
 * Lay `defs` out in `container` at `zoom` (1 = life size). Returns a
 * rezoom for when the zoom changes; empty defs show an empty-state note,
 * worded for a project whose only blocks were library imports
 * (`libraryBlocks`, the renderer's count of what it left out).
 */
export const layoutPics = (
  container: HTMLElement,
  defs: DefinitionPic[],
  zoom: number,
  libraryBlocks = 0,
): { rezoom(zoom: number): void } => {
  const shown: { pic: DefinitionPic; img: HTMLImageElement }[] = [];
  if (defs.length === 0) {
    container.replaceChildren(
      el(
        'div',
        libraryBlocks > 0 ? 'only library blocks so far' : 'no custom blocks or scripts yet',
        'empty-state',
      ),
    );
  } else {
    container.replaceChildren(
      ...defs.map((pic) => {
        const holder = el('div');
        const owner = ownerTag(pic);
        if (owner) holder.appendChild(el('div', owner, 'owner-tag'));
        const img = el('img');
        img.src = pic.dataUrl;
        img.alt = pic.name;
        img.title = pic.name;
        img.style.width = displayWidth(pic, zoom);
        holder.appendChild(img);
        shown.push({ pic, img });
        return holder;
      }),
    );
  }
  return {
    rezoom(z) {
      for (const { pic, img } of shown) img.style.width = displayWidth(pic, z);
    },
  };
};
